function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import memoize from 'memoize-one';
import { ContextActions, createXComponent, LoadingOverlay, Tooltip } from '@deephaven/components';
import { assertNotNull, EMPTY_ARRAY } from '@deephaven/utils';
import Log from '@deephaven/log';
import LayoutUtils from "./layout/LayoutUtils.js";
import PanelEvent from "./PanelEvent.js";
import PanelContextMenu from "./PanelContextMenu.js";
import RenameDialog from "./RenameDialog.js";
import TabEvent from "./TabEvent.js";
import "./Panel.css";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var log = Log.module('Panel');
/**
 * Generic panel component that emits mount/unmount/focus events.
 * Also wires up some triggers for common events:
 * Focus, Resize, Show
 */
class BasePanel extends PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "ref", void 0);
    _defineProperty(this, "getAdditionalActions", memoize((actions, isClonable, isRenamable) => {
      var additionalActions = [];
      if (isClonable) {
        additionalActions.push(this.getCloneAction());
      }
      if (isRenamable) {
        additionalActions.push(this.getRenameAction());
      }
      return [...additionalActions, ...actions];
    }));
    this.handleCopyPanel = this.handleCopyPanel.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleHide = this.handleHide.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleBeforeShow = this.handleBeforeShow.bind(this);
    this.handleShow = this.handleShow.bind(this);
    this.handleTabBlur = this.handleTabBlur.bind(this);
    this.handleTabFocus = this.handleTabFocus.bind(this);
    this.handleRenameCancel = this.handleRenameCancel.bind(this);
    this.handleRenameSubmit = this.handleRenameSubmit.bind(this);
    this.handleShowRenameDialog = this.handleShowRenameDialog.bind(this);
    this.handleTabClicked = this.handleTabClicked.bind(this);
    this.handleTab = this.handleTab.bind(this);
    this.ref = /*#__PURE__*/React.createRef();
    var glContainer = this.props.glContainer;
    this.state = {
      title: LayoutUtils.getTitleFromContainer(glContainer),
      showRenameDialog: false,
      isWithinPanel: true
    };
  }
  componentDidMount() {
    var _this$ref$current;
    var _this$props = this.props,
      componentPanel = _this$props.componentPanel,
      glContainer = _this$props.glContainer,
      glEventHub = _this$props.glEventHub;
    glContainer.on('resize', this.handleResize);
    glContainer.on('show', this.handleBeforeShow);
    glContainer.on('shown', this.handleShow);
    glContainer.on('hide', this.handleHide);
    glContainer.on('tab', this.handleTab);
    glContainer.on('tabClicked', this.handleTabClicked);
    glEventHub.on(TabEvent.focus, this.handleTabFocus);
    glEventHub.on(TabEvent.blur, this.handleTabBlur);
    glEventHub.emit(PanelEvent.MOUNT, componentPanel !== null && componentPanel !== void 0 ? componentPanel : this);
    this.setState({
      isWithinPanel: ((_this$ref$current = this.ref.current) === null || _this$ref$current === void 0 || (_this$ref$current = _this$ref$current.parentElement) === null || _this$ref$current === void 0 ? void 0 : _this$ref$current.closest('.dh-panel')) != null
    });
  }
  componentWillUnmount() {
    var _this$props2 = this.props,
      componentPanel = _this$props2.componentPanel,
      glContainer = _this$props2.glContainer,
      glEventHub = _this$props2.glEventHub;
    glContainer.off('resize', this.handleResize);
    glContainer.off('show', this.handleBeforeShow);
    glContainer.off('shown', this.handleShow);
    glContainer.off('hide', this.handleHide);
    glContainer.off('tab', this.handleTab);
    glContainer.off('tabClicked', this.handleTabClicked);
    glEventHub.off(TabEvent.focus, this.handleTabFocus);
    glEventHub.off(TabEvent.blur, this.handleTabBlur);
    glEventHub.emit(PanelEvent.UNMOUNT, componentPanel !== null && componentPanel !== void 0 ? componentPanel : this);
  }
  handleTab(tab) {
    if (tab != null) {
      this.setState({
        title: LayoutUtils.getTitleFromTab(tab)
      });
    }
    // render after move can happen before tab event, glTab could be null
    // when tab event is emitted, force a render update
    this.forceUpdate();
    var onTab = this.props.onTab;
    onTab === null || onTab === void 0 || onTab(tab);
  }
  handleTabClicked(e) {
    var onTabClicked = this.props.onTabClicked;
    onTabClicked === null || onTabClicked === void 0 || onTabClicked(e);
  }
  handleFocus(event) {
    var _this$props3 = this.props,
      componentPanel = _this$props3.componentPanel,
      glEventHub = _this$props3.glEventHub;
    glEventHub.emit(PanelEvent.FOCUS, componentPanel !== null && componentPanel !== void 0 ? componentPanel : this);
    var onFocus = this.props.onFocus;
    onFocus === null || onFocus === void 0 || onFocus(event);
  }
  handleBlur(event) {
    var onBlur = this.props.onBlur;
    onBlur === null || onBlur === void 0 || onBlur(event);
  }
  handleHide() {
    var onHide = this.props.onHide;
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    onHide === null || onHide === void 0 || onHide(...args);
  }
  handleResize() {
    var onResize = this.props.onResize;
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    onResize === null || onResize === void 0 || onResize(...args);
  }
  handleBeforeShow() {
    var onBeforeShow = this.props.onBeforeShow;
    for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      args[_key3] = arguments[_key3];
    }
    onBeforeShow === null || onBeforeShow === void 0 || onBeforeShow(...args);
  }
  handleShow() {
    var onShow = this.props.onShow;
    for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      args[_key4] = arguments[_key4];
    }
    onShow === null || onShow === void 0 || onShow(...args);
  }
  handleTabBlur() {
    var onTabBlur = this.props.onTabBlur;
    for (var _len5 = arguments.length, args = new Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
      args[_key5] = arguments[_key5];
    }
    onTabBlur === null || onTabBlur === void 0 || onTabBlur(...args);
  }
  handleTabFocus() {
    var onTabFocus = this.props.onTabFocus;
    for (var _len6 = arguments.length, args = new Array(_len6), _key6 = 0; _key6 < _len6; _key6++) {
      args[_key6] = arguments[_key6];
    }
    onTabFocus === null || onTabFocus === void 0 || onTabFocus(...args);
  }
  handleRenameCancel() {
    this.setState({
      showRenameDialog: false
    });
  }
  handleShowRenameDialog() {
    this.setState({
      showRenameDialog: true
    });
  }
  handleRenameSubmit(newTitle) {
    var glContainer = this.props.glContainer;
    this.setState({
      showRenameDialog: false,
      title: newTitle
    });
    var root = LayoutUtils.getRootFromContainer(glContainer);
    var config = LayoutUtils.getComponentConfigFromContainer(glContainer);
    assertNotNull(config);
    LayoutUtils.renameComponent(root, config, newTitle);
  }
  handleCopyPanel() {
    var _this$props4 = this.props,
      glContainer = _this$props4.glContainer,
      glEventHub = _this$props4.glEventHub;
    var root = LayoutUtils.getRootFromContainer(glContainer);
    var config = LayoutUtils.getComponentConfigFromContainer(glContainer);
    if (config == null) {
      log.error('Could not get component config from container', glContainer);
      return;
    }
    var cloneConfig = LayoutUtils.cloneComponent(root, config);
    if (cloneConfig !== null) {
      glEventHub.emit(PanelEvent.CLONED, this, cloneConfig);
    }
  }
  getCloneAction() {
    return {
      title: 'Copy Panel',
      order: 10,
      group: ContextActions.groups.high,
      action: this.handleCopyPanel
    };
  }
  getRenameAction() {
    return {
      title: 'Rename',
      order: 10,
      group: ContextActions.groups.medium,
      action: this.handleShowRenameDialog
    };
  }
  render() {
    var _this$props5 = this.props,
      children = _this$props5.children,
      className = _this$props5.className,
      renderTabTooltip = _this$props5.renderTabTooltip,
      glContainer = _this$props5.glContainer,
      glEventHub = _this$props5.glEventHub,
      _this$props5$addition = _this$props5.additionalActions,
      additionalActions = _this$props5$addition === void 0 ? EMPTY_ARRAY : _this$props5$addition,
      errorMessage = _this$props5.errorMessage,
      _this$props5$isLoaded = _this$props5.isLoaded,
      isLoaded = _this$props5$isLoaded === void 0 ? true : _this$props5$isLoaded,
      _this$props5$isLoadin = _this$props5.isLoading,
      isLoading = _this$props5$isLoadin === void 0 ? false : _this$props5$isLoadin,
      _this$props5$isClonab = _this$props5.isClonable,
      isClonable = _this$props5$isClonab === void 0 ? false : _this$props5$isClonab,
      _this$props5$isRenama = _this$props5.isRenamable,
      isRenamable = _this$props5$isRenama === void 0 ? false : _this$props5$isRenama,
      onErrorAction = _this$props5.onErrorAction;
    var glTab = glContainer.tab;
    var _this$state = this.state,
      showRenameDialog = _this$state.showRenameDialog,
      title = _this$state.title,
      isWithinPanel = _this$state.isWithinPanel;
    return /*#__PURE__*/_jsxs("div", {
      className: classNames('h-100 w-100 dh-panel', className),
      onFocusCapture: this.handleFocus,
      onBlurCapture: this.handleBlur,
      ref: this.ref,
      children: [children, /*#__PURE__*/_jsx(LoadingOverlay, {
        "data-testid": "base-panel-loading",
        errorMessage: errorMessage,
        isLoaded: isLoaded,
        isLoading: isLoading,
        onErrorAction: onErrorAction
      }), !isWithinPanel && glTab != null && /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(PanelContextMenu, {
          glContainer: glContainer,
          glEventHub: glEventHub,
          additionalActions: this.getAdditionalActions(additionalActions, isClonable, isRenamable)
        }), isRenamable && /*#__PURE__*/_jsx(RenameDialog, {
          isShown: showRenameDialog,
          value: title,
          itemType: "Panel",
          onCancel: this.handleRenameCancel,
          onSubmit: this.handleRenameSubmit
        }), renderTabTooltip != null && /*#__PURE__*/_jsx(Tooltip, {
          interactive: true,
          options: {
            placement: 'bottom'
          },
          popperClassName: "panel-tab-popper",
          children: renderTabTooltip()
        })]
      }), glTab.element[0] // tab.element is jquery element, we want a dom element
      )]
    });
  }
}
var XBasePanel = createXComponent(BasePanel);
export default XBasePanel;
//# sourceMappingURL=BasePanel.js.map